import { useCallback, useMemo, useState } from "react";

import type { DropdownTab } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import useStatefulDebouncedCallback from "app/pages/sales-intelligence/hooks/useStatefulDebouncedCallback";
import type { DropdownBaseListItem } from "app/pages/sales-intelligence/modules/technology/types/dropdown";
import {
    dropdownCompareByPrimaryNameMatch,
    dropdownNameMatchesSearchTerm,
    toDropdownItemWithMatchedSubStrings,
} from "app/pages/sales-intelligence/modules/technology/utils/dropdown";

type SearchProps = {
    value: string;
    placeholder: string;
    onChange(value: string): void;
    isFocused?: boolean;
};

type HookOptions = {
    shouldProcessItems?: boolean;
};

type HookParams<T> = {
    tabs: readonly DropdownTab<T>[];
    onItemsSort?<I extends DropdownBaseListItem<string>>(items: ReadonlyArray<I>): ReadonlyArray<I>;
    onNoResultsFound?(query: string): void;
    searchPlaceholder?: string;
};

type HookValue = {
    tabsWithSortedItems: readonly DropdownTab<unknown>[];
    searchTerm: string;
    onSearch(query: string): void;
    searchProps: SearchProps;
};

const DEFAULT_OPTIONS: HookOptions = {
    shouldProcessItems: true,
};

const useDropdownTabbedSearch = <T>(
    { tabs, onItemsSort, onNoResultsFound, searchPlaceholder }: HookParams<T>,
    options: HookOptions = DEFAULT_OPTIONS,
): HookValue => {
    const [searchTerm, setSearchTerm] = useState("");

    /**
     * The main transformer function for each array of items.
     * It filters out all unmatched items.
     * Then it maps them to objects that contain matched string.
     * And sorts by matched string priority, meaning that primary string is coming first.
     */
    const processItemsAccordingToSearchTerm = (term: string) => {
        /** SearchTerm filter predicate */
        const matchesSearchTerm = dropdownNameMatchesSearchTerm(term);
        /** SearchTerm mapper to make matches bold */
        const toItemWithSubStringMatches = toDropdownItemWithMatchedSubStrings(term);

        return <T extends DropdownBaseListItem<string>>(items: readonly T[]): readonly T[] =>
            items.filter(matchesSearchTerm).map(toItemWithSubStringMatches).sort(dropdownCompareByPrimaryNameMatch);
    };

    const sortTabItems = <T extends DropdownBaseListItem<string>>(items: readonly T[]): readonly T[] => {
        if (typeof onItemsSort === "function") {
            return onItemsSort(items);
        }

        return items.slice().sort(dropdownCompareByPrimaryNameMatch);
    };

    const processedItems = useMemo(() => {
        if (!options.shouldProcessItems) {
            return tabs;
        }

        const isSearching = searchTerm.length > 0;
        const processItemsFn = isSearching ? processItemsAccordingToSearchTerm(searchTerm) : sortTabItems;

        return tabs.map((tab) => {
            const filteredItems = processItemsFn(tab.items as readonly DropdownBaseListItem<string>[]);

            return {
                ...tab,
                name: isSearching ? tab.name.replace(/\(\d+\)/, `(${filteredItems.length})`) : tab.name,
                items: filteredItems,
            };
        });
    }, [searchTerm, tabs, options.shouldProcessItems]);

    const handleNoResultsDebounced = useStatefulDebouncedCallback(
        processedItems,
        (query: string, latestSortedItems: Array<unknown>) => {
            if (query.length === 0) {
                return;
            }

            const isNoQueryResultsAvailable = latestSortedItems.every(
                (sortedItems: Array<unknown>) => sortedItems.length === 0,
            );

            if (isNoQueryResultsAvailable && typeof onNoResultsFound === "function") {
                onNoResultsFound(query);
            }
        },
    );

    const onSearch = useCallback(
        (query: string) => {
            setSearchTerm(query);
            handleNoResultsDebounced(query);
        },
        [handleNoResultsDebounced],
    );

    const searchProps: SearchProps = useMemo(() => {
        const placeholder = searchPlaceholder ?? translate("si.lead_gen_filters.technologies.dd_placeholder");

        return {
            value: searchTerm,
            onChange: onSearch,
            placeholder: placeholder,
        };
    }, [searchTerm, onSearch, searchPlaceholder]);

    return { tabsWithSortedItems: processedItems, searchTerm, onSearch, searchProps };
};

export default useDropdownTabbedSearch;
